import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Minus, Plus } from "lucide-react";
import { message } from "antd";
import foodApiService from "../../services/foodApiService";
import welcomeBg from "../../assets/homebg.png";

interface FoodItem {
  id: number;
  food_item_id?: string;
  name: string;
  calories: number;
  weight: number;
  servings: number;
  image: string;
  carbohydrate_content_g?: number;
  protein_content_g?: number;
  fat_content_g?: number;
  dietary_fiber_g?: number;
  net_carbs_g?: number;
  sugar_g?: number;
  trans_fat_g?: number;
  saturated_fat_g?: number;
  GI_estimate?: number;
  GL_estimate?: number;
  serving_description?: string;
}

const EditFoodPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const foodItem = location.state?.foodItem as FoodItem;

  const [editedData, setEditedData] = useState({
    name: "",
    servingDescription: "",
    servings: 0,
    weight: 0,
    calories: 0,
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (foodItem) {
      setEditedData({
        name: foodItem.name,
        servingDescription: foodItem.serving_description ?? "",
        servings: foodItem.servings,
        weight: foodItem.weight,
        calories: foodItem.calories,
      });
    }
  }, [foodItem]);

  const baseCaloriePerServing = foodItem?.calories
    ? foodItem.calories / (foodItem.servings || 1)
    : 0;

  const handleSave = async () => {
    // Validate required fields
    if (!editedData.name.trim()) {
      message.error("Please enter food name");
      return;
    }

    setIsLoading(true);

    try {
      // Prepare API payload according to edit API structure
      const apiPayload = {
        name: editedData.name.trim(),
        serving_size_g: editedData.weight || editedData.servings * 100,
        serving_description:
          editedData.servingDescription.trim() ||
          `${editedData.servings} serving(s)`,
      };

      // Call the edit API
      const response = await foodApiService.editManualFood(apiPayload);

      message.success("Food item updated successfully!");

      // Create updated food item object with API response data
      const updatedFoodItem = {
        ...foodItem,
        name: editedData.name.trim(),
        serving_description: editedData.servingDescription.trim(),
        servings: editedData.servings,
        weight: editedData.weight,
        calories: response.data?.calories || editedData.calories,
        // Update nutrition data if provided by API
        protein_content_g:
          response.data?.protein_content_g || foodItem.protein_content_g,
        carbohydrate_content_g:
          response.data?.carbohydrate_content_g ||
          foodItem.carbohydrate_content_g,
        fat_content_g: response.data?.fat_content_g || foodItem.fat_content_g,
        sugar_g: response.data?.sugar_g || foodItem.sugar_g,
        dietary_fiber_g:
          response.data?.dietary_fiber_g || foodItem.dietary_fiber_g,
      };

      // Navigate back to scanned-food-detail with updated item
      navigate("/glucera/scanned-food-detail", {
        state: {
          localUpdatedItem: updatedFoodItem,
          fromEditPage: true,
        },
      });
    } catch (error: any) {
      message.error(error.message || "Failed to update food item");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (!foodItem) {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[400px] items-center justify-center bg-background">
        <div className="text-foreground">No food item data found</div>
      </div>
    );
  }

  return (
    <div className="mx-auto min-h-screen w-full max-w-[400px] bg-white">
      <div className="space-y-6 p-4">
        {/* Header */}
        <div className="mb-6 flex items-center">
          <button
            onClick={handleBack}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <h1 className="ml-4 text-lg font-semibold text-gray-900">
            Edit Food Item
          </h1>
        </div>

        {/* Food Name */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Food Name</label>
          <input
            type="text"
            value={editedData.name}
            onChange={e =>
              setEditedData({
                ...editedData,
                name: e.target.value,
              })
            }
            className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter food name"
            maxLength={100}
          />
        </div>

        {/* Serving Size (Read-only) */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Serving Size (Read-only)
          </label>
          <div className="w-full rounded-lg border border-gray-200 bg-gray-100 px-4 py-3 text-gray-600">
            {editedData.servingDescription ||
              foodItem.serving_description ||
              `${editedData.weight || foodItem.weight}g`}
          </div>
          <p className="text-xs text-gray-500">
            Serving size is auto-detected and cannot be changed
          </p>
        </div>

        {/* Current Nutrition Info Card */}
        <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h3 className="mb-3 text-sm font-medium text-gray-800">
            Current Nutrition Info
          </h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Calories:</span>
              <span className="font-medium text-gray-900">
                {Math.round(editedData.calories || foodItem.calories || 0)} kcal
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Protein:</span>
              <span className="font-medium text-gray-900">
                {Math.round(foodItem.protein_content_g || 0)}g
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Carbs:</span>
              <span className="font-medium text-gray-900">
                {Math.round(foodItem.carbohydrate_content_g || 0)}g
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Fat:</span>
              <span className="font-medium text-gray-900">
                {Math.round(foodItem.fat_content_g || 0)}g
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Sugar:</span>
              <span className="font-medium text-gray-900">
                {Math.round(foodItem.sugar_g || 0)}g
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Fiber:</span>
              <span className="font-medium text-gray-900">
                {Math.round(foodItem.dietary_fiber_g || 0)}g
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3 pt-4">
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="w-full rounded-lg bg-blue-600 py-3 font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-400"
          >
            {isLoading ? "Saving Changes..." : "Save Changes"}
          </button>

          <button
            onClick={handleBack}
            className="w-full rounded-lg border border-gray-300 bg-white py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditFoodPage;
